import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { getDefaultClass, listClasses, readableApiError, formatClassSubtitle } from '../../api/dashboard';
import { useAuth } from '../../context/AuthContext';
import './dashboard.css';

export default function ClassListPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [resolvingDefault, setResolvingDefault] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // 从企业选择器进入时带 ?enterprise=<id>：只看该企业班级，并显示返回入口。
  const enterpriseId = searchParams.get('enterprise');
  const enterpriseName = location.state?.enterpriseName;

  // default-class-flat:讲师 / 企业管理员直接跳默认班,不渲染选择器。
  // admin / superadmin 跳过(他们要跨企业看,后端会返 404,前端继续渲染列表)。
  useEffect(() => {
    const role = user?.role;
    if (role !== 'instructor' && role !== 'enterprise_admin') {
      setResolvingDefault(false);
      return;
    }
    let cancelled = false;
    getDefaultClass()
      .then((d) => {
        if (cancelled) return;
        const id = d?.class?.id;
        if (id != null) {
          navigate(`/dashboard/classes/${id}`, { replace: true });
        } else {
          setResolvingDefault(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // 默认班解析失败(404 / 网络) → 退回选择器
        setResolvingDefault(false);
      });
    return () => { cancelled = true; };
  }, [user?.role, navigate]);

  useEffect(() => {
    if (resolvingDefault) return;
    listClasses(enterpriseId ? { enterprise: enterpriseId } : undefined)
      .then(setData)
      .catch((err) => setError(readableApiError(err, '加载班级列表失败')));
  }, [resolvingDefault, enterpriseId]);

  if (resolvingDefault) {
    return (
      <div className="aiq-dash dash-page">
        <div className="dash-card">正在打开看板...</div>
      </div>
    );
  }

  return (
    <div className="aiq-dash dash-page">
      {enterpriseId && (
        <Link to="/dashboard/enterprises" className="dash-back">← 返回企业选择</Link>
      )}
      <div className="dash-head">
        <h1 className="dash-h1">{enterpriseName ? `${enterpriseName} · 班级列表` : '班级列表'}</h1>
        <p className="dash-sub">讲师看自己授课的班级；企业管理员看本企业全部班级。</p>
      </div>

      {error && <div className="dash-card dash-card--error">{error}</div>}
      {!data && !error && <div className="dash-card">加载中...</div>}
      {data && data.results.length === 0 && (
        <div className="dash-card">
          <div style={{ fontWeight: 600, marginBottom: 6 }}>暂无班级</div>
          <div className="dash-mut">当你被分配为讲师或企业管理员后，该班级会出现在这里。</div>
        </div>
      )}
      {data && data.results.length > 0 && (
        <>
          <p className="dash-mut">
            共 {data.count ?? data.results.length} 个班级
            {/* TODO M22: 后端配 pagination_class 后接入翻页（list envelope 已就位） */}
          </p>
          <div className="dash-list">
            {data.results.map((cls) => (
              <Link key={cls.id} to={`/dashboard/classes/${cls.id}`} className="dash-list-card">
                <div>
                  <div className="dash-list-card__name">{cls.name}</div>
                  <div className="dash-mut">{formatClassSubtitle(cls)}</div>
                </div>
                <div>
                  <div className="dash-list-card__stat">
                    {cls.completed_count} / {cls.member_count}
                  </div>
                  <div className="dash-mut" style={{ textAlign: 'right' }}>已完成 / 总人数</div>
                </div>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
